import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ErrorText, Field, GroupedCode, LockButton, Page, Submit, passwordProblem } from '../components';
import { saveTextFile } from '../lib/files';
import { useAction, useDevice, useSnapshot } from '../state';

const groups = (id: string) => (id.match(/.{1,4}/g) ?? []).join(' ');

export function Security() {
  const device = useDevice();
  const { settings, deviceId } = useSnapshot();
  const backup = useAction();
  const reminders = useAction();
  const code = useAction();
  const password = useAction();
  const forget = useAction();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [changed, setChanged] = useState(false);
  const [confirmForget, setConfirmForget] = useState(false);

  const download = () =>
    void backup.run(async () => {
      const { filename, text } = await device.exportBackup();
      saveTextFile(filename, text);
    });

  const changePassword = (event: FormEvent) => {
    event.preventDefault();
    const problem = passwordProblem(form.password, form.confirm);
    if (problem) return password.setError(problem);
    void password.run(async () => {
      await device.changePassword(form.password);
      setForm({ password: '', confirm: '' });
      setChanged(true);
    });
  };

  return (
    <Page actions={<LockButton />}>
      <h1>Backup and security</h1>

      <section className="card">
        <h2>Backup</h2>
        <p className="muted" data-testid="last-backup">
          {settings.lastBackupAt ? `Last backup: ${new Date(settings.lastBackupAt).toLocaleString()}` : 'No backup yet.'}
        </p>
        <p>The backup file is encrypted. It opens with your password or your recovery code.</p>
        <ErrorText error={backup.error} />
        <button type="button" className="button" disabled={backup.busy} onClick={download}>
          {backup.busy ? 'Preparing…' : 'Download a backup file'}
        </button>
        <label className="check">
          <input
            type="checkbox"
            checked={settings.backupReminders}
            onChange={(e) => void reminders.run(() => device.setBackupReminders(e.target.checked, settings.backupEveryDays))}
          />
          Remind me to back up
        </label>
        <div className="field">
          <label htmlFor="backup-every">Remind me every</label>
          <select
            id="backup-every"
            value={settings.backupEveryDays}
            disabled={!settings.backupReminders}
            onChange={(e) => void reminders.run(() => device.setBackupReminders(true, Number(e.target.value)))}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
        <ErrorText error={reminders.error} />
      </section>

      <section className="card">
        <h2>Recovery code</h2>
        <p className="muted">A new code replaces the old one on this device. Backups you already made still open with the code you had then.</p>
        <ErrorText error={code.error} />
        <button type="button" className="button secondary" disabled={code.busy} onClick={() => void code.run(() => device.newRecoveryCode())}>
          Make a new recovery code
        </button>
      </section>

      <section className="card">
        <h2>Change password</h2>
        <form onSubmit={changePassword} noValidate>
          <Field label="New password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Field label="Confirm new password" type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          <ErrorText error={password.error} />
          {changed ? <p role="status">Your password has changed. Your other devices will ask you to sign in again.</p> : null}
          <Submit busy={password.busy} busyText="Changing…">
            Change password
          </Submit>
        </form>
      </section>

      <section className="card">
        <h2>This device</h2>
        <p className="muted">Device fingerprint, for checking in person:</p>
        <GroupedCode code={deviceId ? groups(deviceId) : ''} separator=" " label="Device fingerprint" testId="device-id" />
        {confirmForget ? (
          <div className="stack">
            <p>This deletes this account's data from this device. Without a backup file it cannot be brought back.</p>
            <ErrorText error={forget.error} />
            <button type="button" className="button danger" onClick={() => void forget.run(() => device.forgetDevice())}>
              Remove from this device
            </button>
            <button type="button" className="button secondary" onClick={() => setConfirmForget(false)}>
              Keep it
            </button>
          </div>
        ) : (
          <button type="button" className="button danger" onClick={() => setConfirmForget(true)}>
            Remove this account from this device
          </button>
        )}
      </section>

      <div className="links">
        <Link to="/">Back</Link>
      </div>
    </Page>
  );
}
